import { Box, Divider, Typography } from "@mui/material";
import type { FC, ReactElement, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { CONTROL_HEIGHT, FONT_SIZE, INK, LINE_HEIGHT } from "@/theme/tokens";
import { SystemRoundedCheckbox } from "@/ui/Clickable/SystemRoundedCheckbox";
import { SystemDropdown } from "@/ui/Inputs/SystemDropDown";
import { InlineHint } from "@/ui/molecules/InlineHint";
import { SystemBanner } from "@/ui/molecules/SystemBanner";
import { V2_EMR_AREA_FIELD_WIDTH, V2_EMR_ROOMS_FIELD_MIN_WIDTH } from "./consts";
import type { LocationEmrMappingLayout, LocationEmrMappingSectionProps } from "./LocationEmrMappingSection";

type Props = Omit<Extract<LocationEmrMappingSectionProps, { design: "v2" }>, "design" | "layout"> & { layout: LocationEmrMappingLayout };

/**
 * The v2 census area: "Department wide census" and what it means, the unit and floor, and the rooms — locked
 * while the whole department is chosen in a ratio department, as in v1. It also says why the rooms aren't there yet (loading, or failed to
 * load), which v1 leaves to its host.
 *
 * `stacked`: Department wide with its helper, over the unit and floor side by side (stacked on a phone), over the
 * rooms across the full width. `row`: Department wide, the unit, the floor and the rooms on one line, the helper
 * under it; the rooms wrap to their own line when the line runs out. On a phone Department wide and the rooms each
 * take a line, and the unit and floor share one.
 */
export const LocationEmrMappingSectionV2: FC<Props> = ({
	availableUnits,
	availableFloors,
	availableRooms,
	mapping,
	departmentWideCensus,
	onDepartmentWideCensusChange,
	lockAreasWhenDepartmentWide,
	onMappingChange,
	onDropdownOpen,
	onDropdownClose,
	roomsNotice,
	layout,
	disabled,
}): ReactElement => {
	const { t } = useTranslation();
	const noneOption = { value: "", label: t("schedule.template.v3.editor.none") };
	const areasDisabled = lockAreasWhenDepartmentWide && departmentWideCensus;
	const showUnits = availableUnits.length > 0;
	const showFloors = availableFloors.length > 0;
	const showRooms = availableRooms.length > 0;

	const departmentWide = (
		<SystemRoundedCheckbox
			checked={departmentWideCensus}
			onChange={onDepartmentWideCensusChange}
			label={t("schedule.template.census.department.wide")}
			disabled={disabled}
			data-testid="location-department-wide-census-checkbox"
		/>
	);
	const helper = (
		<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED, lineHeight: LINE_HEIGHT.DENSE_PROSE }}>
			{t(departmentWideCensus ? "schedule.template.census.department.wide.helper" : "schedule.template.census.area.helper")}
		</Typography>
	);
	const unitField = showUnits && (
		<SystemDropdown
			onOpen={onDropdownOpen}
			onClose={onDropdownClose}
			label={t("schedule.template.v3.editor.unit")}
			fullWidth
			disabled={areasDisabled || disabled}
			items={[noneOption, ...availableUnits.map((unit) => ({ value: unit, label: unit }))]}
			// DEFAULT: no unit selects the "None" option, whose value is "".
			selectedItem={mapping.unit ?? ""}
			onChange={(item) => onMappingChange({ unit: item === "" ? undefined : item })}
			data-testid="location-emr-unit-dropdown"
		/>
	);
	const floorField = showFloors && (
		<SystemDropdown
			onOpen={onDropdownOpen}
			onClose={onDropdownClose}
			label={t("schedule.template.v3.editor.floor")}
			fullWidth
			disabled={areasDisabled || disabled}
			items={[noneOption, ...availableFloors.map((floor) => ({ value: floor, label: floor }))]}
			// DEFAULT: no floor selects the "None" option, whose value is "".
			selectedItem={mapping.floor ?? ""}
			onChange={(item) => onMappingChange({ floor: item === "" ? undefined : item })}
			data-testid="location-emr-floor-dropdown"
		/>
	);
	const roomsField = showRooms && (
		<SystemDropdown
			multiple
			search
			label={t("schedule.template.v3.editor.rooms")}
			fullWidth
			disabled={areasDisabled || disabled}
			items={availableRooms.map((room) => ({ value: room, label: room }))}
			selectedItems={mapping.rooms}
			onChangeMultiple={(rooms) => onMappingChange({ rooms })}
			onOpen={onDropdownOpen}
			onClose={onDropdownClose}
			data-testid="location-emr-rooms-dropdown"
		/>
	);
	const notices = (
		<>
			{roomsNotice === "rooms_loading" && (
				<InlineHint tone="neutral" dataTestId="location-emr-rooms-loading">
					{t("schedule.template.census.scope.rooms.loading")}
				</InlineHint>
			)}
			{roomsNotice === "rooms_failed" && (
				<SystemBanner
					tone="error"
					title={t("locations.emrEdit.roomsError.title")}
					description={t("schedule.template.census.scope.rooms.failed")}
					dataTestId="location-emr-rooms-error"
				/>
			)}
		</>
	);

	if (layout === "row") {
		return <EmrRow departmentWide={departmentWide} helper={helper} notices={notices} unitField={unitField} floorField={floorField} roomsField={roomsField} />;
	}
	return (
		<Box data-testid="location-emr-section" sx={{ display: "flex", flexDirection: "column", gap: 1.5, width: "100%", minWidth: 0 }}>
			<Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
				{departmentWide}
				{helper}
			</Box>
			{(showUnits || showFloors) && (
				<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: showUnits && showFloors ? "1fr 1fr" : "1fr" }, gap: 1.5 }}>
					{unitField}
					{floorField}
				</Box>
			)}
			{roomsField}
			{notices}
		</Box>
	);
};

type EmrRowProps = {
	departmentWide: ReactNode;
	helper: ReactNode;
	notices: ReactNode;
	/** `false` when the EMR lists offer none. */
	unitField: ReactNode;
	floorField: ReactNode;
	roomsField: ReactNode;
};

/**
 * The `row` layout. Unit and floor keep a set width beside Department wide; the rooms take what the line has left,
 * down to their minimum, then wrap. On a phone the unit and floor split a line and the rest take one each. A hairline between Department wide
 * and the area fields says the two are either-or.
 */
const EmrRow: FC<EmrRowProps> = ({ departmentWide, helper, notices, unitField, floorField, roomsField }): ReactElement => {
	const hasAreaField = unitField !== false || floorField !== false || roomsField !== false;
	const areaFieldSx = { flex: { xs: "1 1 0%", sm: `0 1 ${V2_EMR_AREA_FIELD_WIDTH}` }, minWidth: 0 };
	return (
		<Box data-testid="location-emr-section" sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%", minWidth: 0 }}>
			<Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: 2, rowGap: 1.5, minWidth: 0 }}>
				<Box sx={{ flex: { xs: "1 1 100%", sm: "0 0 auto" }, display: "flex", alignItems: "center", minHeight: CONTROL_HEIGHT.STANDARD }}>{departmentWide}</Box>
				{hasAreaField && <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" }, my: 1 }} />}
				{unitField !== false && <Box sx={areaFieldSx}>{unitField}</Box>}
				{floorField !== false && <Box sx={areaFieldSx}>{floorField}</Box>}
				{roomsField !== false && <Box sx={{ flex: { xs: "1 1 100%", sm: `1 1 ${V2_EMR_ROOMS_FIELD_MIN_WIDTH}` }, minWidth: 0 }}>{roomsField}</Box>}
			</Box>
			{helper}
			{notices}
		</Box>
	);
};
